import { lazy, Suspense, useEffect, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import DevBeep from "./components/DevBeep";
import { captureUtmParams } from "@/utils/utm";
import RequireFriend from "./pages/skin/RequireFriend";
import {
  capture as phCapture,
  capturePageview as phCapturePageview,
  disableSessionRecording as phDisableRecording,
  enableSessionRecording as phEnableRecording,
} from "@/lib/posthog";

// Critical: load eagerly
import Index from "./pages/Index";

// Lazy with retry: after a deploy, hashed chunks referenced by an already-loaded
// index.js no longer exist on the CDN. Detect that and force a one-time reload
// so the browser fetches the new index.html + new chunk hashes.
const RELOAD_KEY = "lovable:chunk-reload";
function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err);
      const isChunkErr =
        /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError/i.test(msg);
      if (isChunkErr && typeof window !== "undefined") {
        const already = sessionStorage.getItem(RELOAD_KEY);
        if (!already) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Return a never-resolving promise while reload happens.
          return await new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}

// Lazy-loaded pages for code splitting
const ProductDetails = lazyWithRetry(() => import("./pages/ProductDetails"));
const BrandStory = lazyWithRetry(() => import("./pages/BrandStory"));
const Articles = lazyWithRetry(() => import("./pages/Articles"));
const ArticleDetail = lazyWithRetry(() => import("./pages/ArticleDetail"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const ReturnPolicy = lazyWithRetry(() => import("./pages/ReturnPolicy"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const OrderConfirmation = lazyWithRetry(() => import("./pages/OrderConfirmation"));
const PaymentResult = lazyWithRetry(() => import("./pages/PaymentResult"));
const Pay = lazyWithRetry(() => import("./pages/Pay"));
const AISupport = lazyWithRetry(() => import("./pages/AISupport"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const Promotion = lazyWithRetry(() => import("./pages/Promotion"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminSeoPipeline = lazyWithRetry(() => import("./pages/AdminSeoPipeline"));
const AdminSeoWizard = lazyWithRetry(() => import("./pages/AdminSeoWizard"));
const AdminSeoMonitor = lazyWithRetry(() => import("./pages/AdminSeoMonitor"));
const KolPartnership = lazyWithRetry(() => import("./pages/KolPartnership"));
const KolRedirect = lazyWithRetry(() => import("./pages/KolRedirect"));
const LineRedirect = lazyWithRetry(() => import("./pages/LineRedirect"));
const KolReport = lazyWithRetry(() => import("./pages/KolReport"));

const MyCoupons = lazyWithRetry(() => import("./pages/MyCoupons"));
const MyOrders = lazyWithRetry(() => import("./pages/MyOrders"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SeoGuide = lazyWithRetry(() => import("./pages/SeoGuide"));

const Diagnostics = lazyWithRetry(() => import("./pages/Diagnostics"));
const SkinAnalysisHome = lazyWithRetry(() => import("./pages/skin/SkinAnalysisHome"));
const SkinSelectMode = lazyWithRetry(() => import("./pages/skin/SkinSelectMode"));
const SkinUpload = lazyWithRetry(() => import("./pages/skin/SkinUpload"));

const SkinResult = lazyWithRetry(() => import("./pages/skin/SkinResult"));
const SkinHistory = lazyWithRetry(() => import("./pages/skin/SkinHistory"));
const SkinShare = lazyWithRetry(() => import("./pages/skin/SkinShare"));
const SkinAddLine = lazyWithRetry(() => import("./pages/skin/SkinAddLine"));


const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const UtmCapture = () => {
  const location = useLocation();
  useEffect(() => {
    captureUtmParams();

    // PostHog manual pageview (SPA route change).
    try { phCapturePageview(location.pathname + location.search); } catch { /* noop */ }

    // Admin pages: disable Session Replay to avoid recording customer PII
    // shown in admin dialogs (order/user detail). Re-enable elsewhere.
    try {
      if (location.pathname.startsWith('/admin')) {
        phDisableRecording();
      } else {
        phEnableRecording();
      }
    } catch { /* noop */ }

    // Fire KOL referral tracking events (once per session) — ONLY for verified
    // KOL traffic. General campaigns (utm_source=facebook&utm_campaign=9月新品)
    // must never be attributed to a KOL.
    const params = new URLSearchParams(location.search);
    const utmCampaign = params.get('utm_campaign');
    const utmSource = params.get('utm_source') || '';
    if ((utmCampaign || utmSource) && !sessionStorage.getItem('kol_referral_tracked')) {
      (async () => {
        const { extractKolCode, isKolSource, markUtmAsKol } = await import('@/utils/utm');
        let kolId: string | null = null;

        if (isKolSource(utmSource)) {
          kolId = extractKolCode(utmSource) || utmCampaign || null;
        } else if (utmCampaign) {
          // Verify the campaign value is an existing KOL code before tagging it.
          try {
            const { supabase } = await import('@/integrations/supabase/client');
            const { data } = await supabase.functions.invoke('check-kol-code', {
              body: { code: utmCampaign },
            });
            // Only treat as a real KOL code when the response has no `error`
            // (i.e. the code matched the format AND already exists).
            // "Invalid format" responses (available:false + error) must NOT be
            // tagged as KOL, otherwise generic campaign names get misclassified.
            if (data && !data.error && data.available === false) kolId = utmCampaign;
          } catch { /* noop */ }
        }

        if (!kolId) return;
        if (sessionStorage.getItem('kol_referral_tracked')) return;
        sessionStorage.setItem('kol_referral_tracked', '1');
        try { markUtmAsKol(); } catch { /* noop */ }

        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'kol_referral', {
            kol_id: kolId,
            page_location: window.location.href,
          });
        }
        if (typeof window.fbq === 'function') {
          window.fbq('trackCustom', 'KOLReferral', { kol_id: kolId });
        }
        try { phCapture('kol_referral', { kol_id: kolId }); } catch { /* noop */ }
      })();
    }
  }, [location.pathname, location.search]);
  return null;
};

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <CartProvider>
          <Toaster />
          <Sonner />
          {import.meta.env.DEV && <DevBeep />}
          <BrowserRouter>
            <UtmCapture />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/product-details" element={<ProductDetails />} />
                <Route path="/brand-story" element={<BrandStory />} />
                <Route path="/articles/:slug" element={<ArticleDetail />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/payment/result" element={<PaymentResult />} />
                <Route path="/pay" element={<Pay />} />
                <Route path="/ai-support" element={<AISupport />} />
                <Route path="/login" element={<Login />} />
                <Route path="/promotion" element={<Promotion />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/seo/pipeline/:articleId" element={<AdminSeoPipeline />} />
                <Route path="/admin/seo/wizard" element={<AdminSeoWizard />} />
                <Route path="/admin/seo/wizard/:articleId" element={<AdminSeoWizard />} />
                <Route path="/admin/seo/wizard/:articleId/:step" element={<AdminSeoWizard />} />
                <Route path="/admin/seo/monitor" element={<AdminSeoMonitor />} />
                <Route path="/kol" element={<KolPartnership />} />
                <Route path="/kol/report" element={<KolReport />} />
                <Route path="/kol/:code" element={<KolRedirect />} />
                <Route path="/line" element={<LineRedirect />} />
                
                <Route path="/my-coupons" element={<MyCoupons />} />
                <Route path="/my-orders" element={<MyOrders />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/return-policy" element={<ReturnPolicy />} />
                <Route path="/seo-guide" element={<SeoGuide />} />
                
                <Route path="/diagnostics" element={<Diagnostics />} />

                {/* Skin Analysis (F2) */}
                <Route path="/skin-analysis" element={<SkinAnalysisHome />} />
                <Route path="/skin-analysis/add-line" element={<SkinAddLine />} />
                <Route path="/skin-analysis/select-mode" element={<RequireFriend next="/skin-analysis/select-mode"><SkinSelectMode /></RequireFriend>} />
                <Route path="/skin-analysis/upload" element={<RequireFriend next="/skin-analysis/upload"><SkinUpload /></RequireFriend>} />
                
                <Route path="/skin-analysis/result/:id" element={<RequireFriend next="/skin-analysis/history"><SkinResult /></RequireFriend>} />
                <Route path="/skin-analysis/history" element={<RequireFriend next="/skin-analysis/history"><SkinHistory /></RequireFriend>} />
                <Route path="/skin-analysis/share/:token" element={<SkinShare />} />


                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
